import {
  createTheme as createMuiTheme,
  getContrastRatio,
} from '@mui/material/styles';
import { Theme } from '@mui/system';
import { isNil, merge, omitBy } from 'lodash';
import { DeepPartial } from 'utility-types';

import { BAITheme } from '@bai/utils/types';

import defaultLogo from './logo.png';

const defaultBlueColor = '#1EABE3';

export const getContrastTextColor = (backgroundColor: string) => {
  const white = '#FFFFFF';
  const black = '#000000';

  // Handle undefined/null backgroundColor gracefully
  if (!backgroundColor) {
    return black; // Default to black text for undefined backgrounds
  }

  if (backgroundColor?.toLowerCase() === defaultBlueColor.toLowerCase()) {
    // we're allowing white font with default blue even though the ratio is 2.63
    return white;
  }
  const ratio = getContrastRatio(white, backgroundColor);
  // https://www.w3.org/TR/WCAG/#contrast-minimum
  if (ratio > 3) {
    return white;
  }
  return black;
};

const srgbToLinear = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
};

const linearToSrgb = (linear: number) => {
  const normalized =
    linear <= 0.0031308
      ? linear * 12.92
      : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
  // round down (never up) so integer channel quantization can't push the ratio back below target
  return Math.min(255, Math.max(0, Math.floor(normalized * 255)));
};

const getRelativeLuminance = (hex: string) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = srgbToLinear((num >> 16) & 0xff);
  const g = srgbToLinear((num >> 8) & 0xff);
  const b = srgbToLinear(num & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// luminance is a linear combination of linear-light RGB, so scaling those values by `scale`
// scales the resulting luminance by the same exact factor (no gamma approximation involved)
const scaleLuminance = (hex: string, scale: number) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = linearToSrgb(srgbToLinear((num >> 16) & 0xff) * scale);
  const g = linearToSrgb(srgbToLinear((num >> 8) & 0xff) * scale);
  const b = linearToSrgb(srgbToLinear(num & 0xff) * scale);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export const darkenColorIfTooLight = (backgroundColor: string) => {
  const white = '#FFFFFF';
  const black = '#000000';
  const minRatio = 4.5;

  // Handle undefined/null backgroundColor gracefully
  if (!backgroundColor) {
    return black; // Default to black text for undefined backgrounds
  }

  if (backgroundColor?.toLowerCase() === defaultBlueColor.toLowerCase()) {
    // we're allowing white font with default blue even though the ratio is 2.63
    return backgroundColor;
  }

  // https://www.w3.org/TR/WCAG/#contrast-minimum
  const ratio = getContrastRatio(white, backgroundColor);
  if (ratio >= minRatio) {
    return backgroundColor;
  }

  // white's luminance is 1, so contrast ratio = 1.05 / (luminance + 0.05); invert that to
  // find the luminance needed to just clear minRatio, then scale down to match exactly
  const targetLuminance = 1.05 / minRatio - 0.05;
  const currentLuminance = getRelativeLuminance(backgroundColor);
  const scale = targetLuminance / currentLuminance;

  return scaleLuminance(backgroundColor, scale);
};

export const createTheme = (themeOptions: DeepPartial<BAITheme>): Theme => {
  const defaultTheme = {
    typography: {
      /* Here is a pixel <=> rem converter. Make sure to set root font size to 10px first. https://nekocalc.com/px-to-rem-converter */
      htmlFontSize: 10,
      button: {
        fontSize: '1.4rem', // 14 pixels
      },
      caption: {
        fontSize: '1.2rem', // 12 pixels
      },
    },
    shape: {
      borderRadius: 8,
    },
    palette: {
      primary: {
        main: defaultBlueColor,
        contrastText: '#ffffff',
        light: '#1babe3',
        dark: '#0076a8',
      },
      secondary: {
        main: '#eee',
        contrastText: '#505050',
        light: '#ccc',
      },
    },
    branding: {
      logo: defaultLogo,
      backgroundImage: null,
      primaryColor: defaultBlueColor,
      secondaryColor: null,
      tertiaryColor: null,
    },
    additionalStyles: {
      darkestColor: '#00375d',
    },
  };

  const cleanThemeOptions = {
    ...omitBy(themeOptions, isNil),
    branding: {
      ...omitBy(themeOptions?.branding, isNil),
    },
  };

  // TODO: is there a better way to determine whether a non-default color is being used?
  const hasCustomColor = !!(
    themeOptions.branding && themeOptions.branding.primaryColor
  );

  const mergedThemeOptions = merge(
    {},
    defaultTheme,
    cleanThemeOptions,
    {
      palette: {
        primary: {
          contrastText: getContrastTextColor(
            cleanThemeOptions.branding['primaryColor'] ||
              defaultTheme.palette.primary.main,
          ),
        },
      },
    },
    {
      hasCustomColor,
    },
  ) as unknown as BAITheme;
  return createMuiTheme(mergedThemeOptions);
};
